import { useState, useEffect, useRef, useCallback, createElement, useMemo } from 'react';
import { useBadges } from './useBadges';
import toast from 'react-hot-toast';
import { AlertTriangle, AlertOctagon, CheckCircle, Megaphone } from 'lucide-react';

const STORAGE_KEY = 'jpro_dismissed_announcements';

// Store as { id: dismissed_at_timestamp } so we can compare against updated_at
function getDismissedMap() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    // Migrate from old array format [id, id, ...] to map { id: timestamp }
    if (Array.isArray(stored)) {
      const map = {};
      stored.forEach(id => { map[id] = 0; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
      return map;
    }
    return stored;
  } catch {
    return {};
  }
}

export default function useAnnouncements() {
  const { data: badges } = useBadges();
  const announcements = useMemo(() => badges?.announcements || [], [badges]);

  const [dismissedMap, setDismissedMap] = useState(getDismissedMap);
  const seenIdsRef = useRef(new Set());
  const initialFetchDone = useRef(false);

  // Detect newly-arrived announcements and toast them — runs whenever the bundled
  // /badges poll returns fresh data.
  useEffect(() => {
    if (!badges) return;

    if (initialFetchDone.current) {
      const dismissed = getDismissedMap();
      announcements.forEach(a => {
        if (!seenIdsRef.current.has(a.id) && !dismissed[a.id]) {
          const iconMap = {
            warning: createElement(AlertTriangle, { size: 18, color: '#f59e0b' }),
            error: createElement(AlertOctagon, { size: 18, color: '#ef4444' }),
            success: createElement(CheckCircle, { size: 18, color: '#22c55e' }),
          };
          toast(a.title, {
            icon: iconMap[a.announcement_type] || createElement(Megaphone, { size: 18, color: '#3b82f6' }),
            duration: 6000,
            style: { fontFamily: 'Rasa, serif' }
          });
        }
      });
    }

    announcements.forEach(a => seenIdsRef.current.add(a.id));
    initialFetchDone.current = true;

    // Prune dismissed entries for announcements that no longer exist
    const activeIds = new Set(announcements.map(a => a.id));
    setDismissedMap(prev => {
      const pruned = {};
      for (const [id, ts] of Object.entries(prev)) {
        if (activeIds.has(Number(id)) || activeIds.has(id)) {
          pruned[id] = ts;
        }
      }
      if (Object.keys(pruned).length !== Object.keys(prev).length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
      }
      return pruned;
    });
  }, [badges, announcements]);

  const dismiss = useCallback((id) => {
    setDismissedMap(prev => {
      const updated = { ...prev, [id]: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const dismissAll = useCallback(() => {
    const now = Date.now();
    setDismissedMap(prev => {
      const updated = { ...prev };
      announcements.filter(a => a.dismissible).forEach(a => {
        updated[a.id] = now;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, [announcements]);

  // An announcement is visible if it's not dismissed, OR if it was updated after dismissal
  const visible = announcements.filter(a => {
    const dismissedAt = dismissedMap[a.id];
    if (!dismissedAt) return true;
    if (a.updated_at && new Date(a.updated_at).getTime() > dismissedAt) return true;
    return false;
  });
  const unreadCount = visible.length;

  return { announcements: visible, allAnnouncements: announcements, unreadCount, dismiss, dismissAll };
}
